// Solution Finder questions and indicative scoring rules (editable).
export const FINDER_QUESTIONS = [
  {
    id: "connection",
    q: "What type of electricity connection does your facility have?",
    options: [
      { v: "ht", l: "High tension (HT)" },
      { v: "lt", l: "Low tension (LT)" },
      { v: "unsure", l: "Not sure" },
    ],
  },
  {
    id: "space",
    q: "How much usable roof or open space is available on site?",
    options: [
      { v: "large", l: "Large, shade-free areas" },
      { v: "limited", l: "Some, but limited" },
      { v: "none", l: "Little or none" },
    ],
  },
  {
    id: "hours",
    q: "When does your facility mainly consume electricity?",
    options: [
      { v: "day", l: "Mostly daytime" },
      { v: "multi", l: "Two or three shifts" },
      { v: "247", l: "Round the clock" },
    ],
  },
  {
    id: "invest",
    q: "How would you prefer to pay for renewable power?",
    options: [
      { v: "own", l: "Invest and own the asset" },
      { v: "ppa", l: "No upfront investment, pay per unit" },
      { v: "both", l: "Open to both" },
    ],
  },
  {
    id: "share",
    q: "How much of your consumption would you like to cover?",
    options: [
      { v: "part", l: "A meaningful part" },
      { v: "majority", l: "As much as practical" },
    ],
  },
];

const OFFSITE = ["open-access", "third-party", "captive", "group-captive", "hybrid"];

export const FINDER_RULES = [
  { q: "space", a: "large", sol: ["rooftop"], w: 3, why: "You have usable on-site space for a rooftop or ground-mounted plant" },
  { q: "space", a: "limited", sol: ["rooftop"], w: 1, why: "Limited on-site space may still support a smaller rooftop plant" },
  { q: "space", a: "limited", sol: OFFSITE, w: 1, why: "Off-site generation can supplement limited on-site space" },
  { q: "space", a: "none", sol: ["rooftop"], w: -6 },
  { q: "space", a: "none", sol: OFFSITE, w: 2, why: "Off-site generation does not depend on your own roof area" },
  { q: "connection", a: "ht", sol: OFFSITE, w: 2, why: "HT connections are generally the starting point for open access eligibility" },
  { q: "connection", a: "lt", sol: OFFSITE, w: -3 },
  { q: "connection", a: "lt", sol: ["rooftop"], w: 2, why: "On-site solar is often the most accessible option for LT connections" },
  { q: "hours", a: "day", sol: ["rooftop", "open-access", "third-party"], w: 1, why: "Daytime consumption aligns with solar generation hours" },
  { q: "hours", a: "multi", sol: ["hybrid"], w: 2, why: "Wind can add generation beyond daylight hours for multi-shift operations" },
  { q: "hours", a: "247", sol: ["hybrid"], w: 3, why: "A wind-solar blend can cover more of a round-the-clock load" },
  { q: "invest", a: "own", sol: ["captive"], w: 3, why: "You prefer ownership, which captive structures provide" },
  { q: "invest", a: "own", sol: ["rooftop", "group-captive"], w: 1, why: "Can be structured with your own investment" },
  { q: "invest", a: "ppa", sol: ["third-party"], w: 3, why: "A developer invests and you pay per unit under a PPA" },
  { q: "invest", a: "ppa", sol: ["rooftop"], w: 1, why: "Rooftop can be set up under an OPEX / RESCO model" },
  { q: "invest", a: "ppa", sol: ["group-captive"], w: 1, why: "Requires a relatively small equity stake, with power bought from the SPV" },
  { q: "invest", a: "ppa", sol: ["captive"], w: -2 },
  { q: "invest", a: "both", sol: ["rooftop", "open-access", "third-party", "captive", "group-captive", "hybrid"], w: 1 },
  { q: "share", a: "part", sol: ["rooftop"], w: 2, why: "Rooftop is a practical way to cover part of your demand" },
  { q: "share", a: "majority", sol: OFFSITE, w: 2, why: "Off-site projects can supply larger volumes than on-site space allows" },
];

export const FINDER_STORAGE_KEY = "si_finder_profile";

export const saveFinderProfile = (answers, results) => {
  const profile = FINDER_QUESTIONS.filter((q) => answers[q.id]).map((q) => ({
    question: q.q,
    answer: q.options.find((o) => o.v === answers[q.id])?.l ?? "",
  }));
  sessionStorage.setItem(FINDER_STORAGE_KEY, JSON.stringify({ profile, shortlist: results.map((r) => r.title) }));
};

export const loadFinderProfile = () => {
  try {
    const d = JSON.parse(sessionStorage.getItem(FINDER_STORAGE_KEY) || "null");
    return Array.isArray(d?.profile) && d.profile.length ? { profile: d.profile, shortlist: Array.isArray(d.shortlist) ? d.shortlist : [] } : null;
  } catch {
    return null;
  }
};

export const clearFinderProfile = () => sessionStorage.removeItem(FINDER_STORAGE_KEY);

export const scoreFinder = (answers, solutions) => {
  const res = solutions.map((s) => ({ ...s, score: 0, reasons: [] }));
  FINDER_RULES.forEach((r) => {
    if (answers[r.q] !== r.a) return;
    res.forEach((s) => {
      if (!r.sol.includes(s.id)) return;
      s.score += r.w;
      if (r.why && r.w > 0 && !s.reasons.some((x) => x.why === r.why)) s.reasons.push({ why: r.why, w: r.w });
    });
  });
  return res
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((s) => ({ ...s, reasons: s.reasons.sort((a, b) => b.w - a.w).map((x) => x.why) }));
};
